'use client';

import React from 'react';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { useConvert } from '@/api/trading/useConvert';
import { AnimatedText } from '@/blocks/animations/animated-text';
import { Container } from '@/blocks/components/Container';
import { Section } from '@/blocks/components/layout';
import { FormatDate } from '@/blocks/formatters/format-date';
import { useFormatter } from '@/blocks/formatters/format-number';
import typography from '@/styles/typography.module.scss';
import { isFiniteNumber } from '@/utils/number';

import { ConverterCurrency } from '@/app/[lang]/converters/types';

import styles from './styles.module.scss';

type CurrencyExchangeInfoProps = {
  currencyFrom: ConverterCurrency;
  currencyTo: ConverterCurrency;
  initialRate?: number;
};

export function ConvertersExchangeInfo(props: CurrencyExchangeInfoProps) {
  const tr = useTranslations();

  const { rate } = useConvert({
    from: props.currencyFrom.title,
    to: props.currencyTo.title,
    baseAmount: 1,
    fallbackData: props.initialRate ? { rate: props.initialRate } : undefined,
  });

  const formatter = useFormatter({
    maximumFractionDigits: 20,
  });

  return (
    <section className={styles.section}>
      <Container>
        <div className={styles.wrapper}>
          <AnimatedText className={classNames(typography.h2)}>
            {tr.rich('pages.converters.symbol.sections.exchangeInfo.title', {
              value1: (_) => props.currencyFrom.title,
              value2: (_) => props.currencyTo.title,
            })}
          </AnimatedText>
          <p className={typography.subheadingM}>
            {tr.rich('pages.converters.symbol.sections.exchangeInfo.subtitle', {
              asset1FullName: (_) => props.currencyFrom.description,
              asset2FullName: (_) => props.currencyTo.description,
              asset1ShortName: (_) => props.currencyFrom.title,
              asset2ShortName: (_) => props.currencyTo.title,
              rateValue: (_) => formatter.format([rate, props.initialRate, 0].find(isFiniteNumber) as number),
              valueDate: (_) => (
                <FormatDate
                  date={Date.now()}
                  options={{
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  }}
                />
              ),
            })}
          </p>
        </div>
      </Container>
    </section>
  );
}
